import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../AuthService/auth.service';
import { IndexedDbService } from '../IndexedDbService/indexed-db.service';
import { environment } from 'src/environments/environment';
import { JSONSchema } from '@ngx-pwa/local-storage';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseService<T> {
  protected abstract localStorageKey: string;
  protected apiUrl: string = environment.apiUrl;

  constructor(
    protected http: HttpClient,
    protected authService: AuthService,
    protected indexedDbService: IndexedDbService
  ) {}

  // GET HTTP method
  protected get(endpoint: string): Observable<T> {
    const token = this.authService.getToken();
    if (!token) return this.handleNoToken();

    return this.http.get<T>(`${this.apiUrl}${endpoint}`, {
      headers: this.getAuthHeaders(token),
    }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  // POST HTTP method
  protected createItem(endpoint: string, item: T): Observable<T> {
    const token = this.authService.getToken();
    if (!token) return this.handleNoToken();

    return this.http.post<T>(`${this.apiUrl}${endpoint}`, item, {
      headers: this.getAuthHeaders(token),
    }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  // GET HTTP method for arrays
  protected getItems(endpoint: string): Observable<T[]> {
    const token = this.authService.getToken();
    if (!token) return this.handleNoToken();

    return this.http.get<T[]>(`${this.apiUrl}${endpoint}`, {
      headers: this.getAuthHeaders(token),
    }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  // GET from IndexedDB
  getItemsOffline(schema: JSONSchema): Observable<T[]> {
    return this.indexedDbService.get<T[]>(this.localStorageKey, schema, {}).pipe(
      map((items) => items ?? []),
      catchError(err => this.handleIndexedDbError(err, 'buscar'))
    );
  }

  // SAVE to IndexedDB
  saveItem(item: T, schema: JSONSchema): Observable<void> {
    return this.indexedDbService.save<T[]>(
      this.localStorageKey,
      [[item]], // Encapsulado em um array para satisfazer a tipagem T[][]
      schema,
      [item]  // Dados do item para o argumento 'data'
    ).pipe(
      map(() => {}),
      catchError(err => this.handleIndexedDbError(err, 'salvar'))
    );
  }

  // UPDATE IndexedDB
  updateItem(item: T, idProp: keyof T, schema: JSONSchema): Observable<T> {
    return this.getItemsOffline(schema).pipe(
      map((items: T[]) => {
        const index = items.findIndex((i) => i[idProp] === item[idProp]);
        if (index !== -1) {
          items[index] = item;
          this.indexedDbService.save<T[]>(
            this.localStorageKey,
            [items], // Encapsulado em um array para satisfazer a tipagem T[][]
            schema,
            items  // Dados do item para o argumento 'data'
          ).subscribe();
        }
        return item;
      }),
      catchError(err => this.handleIndexedDbError(err, 'atualizar'))
    );
  }

  // DELETE from IndexedDB
  deleteItem(id: any, idProp: keyof T, schema: JSONSchema): Observable<void> {
    return this.getItemsOffline(schema).pipe(
      map((items: T[]) => {
        const updatedItems = items.filter((i) => i[idProp] !== id);
        this.indexedDbService.save<T[]>(
          this.localStorageKey,
          [updatedItems], // Encapsulado em um array para satisfazer a tipagem T[][]
          schema,
          updatedItems  // Dados do item para o argumento 'data'
        ).subscribe();
      }),
      catchError(err => this.handleIndexedDbError(err, 'deletar'))
    );
  }

  // Get authorization headers
  protected getAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  // Handle missing token error
  protected handleNoToken(): Observable<never> {
    console.error('Token não encontrado. Redirecionando para login.');
    return throwError(() => new Error('Token não encontrado'));
  }

  // Handle HTTP errors
  protected handleHttpError(error: any): Observable<never> {
    console.error('Erro na requisição:', error);
    return throwError(() => error);
  }

  // Handle IndexedDB errors
  private handleIndexedDbError(error: any, operation: string): Observable<never> {
    console.error(`Erro ao ${operation} item no IndexedDB:`, error);
    return throwError(() => new Error(`Erro ao ${operation} no IndexedDB`));
  }
}